/**
 * نمودار تحلیلی حرفه‌ای
 * =======================
 * روی همان کتابخانه‌ای ساخته شده که از قبل در پروژه بود
 * (`lightweight-charts`)، ولی با امکاناتی که یک تحلیل‌گر واقعاً لازم دارد:
 *
 *   • هشت نوع نمودار: شمعی، شمع توخالی، هایکن آشی، میله‌ای، خطی، سطحی،
 *     خط پایه، پله‌ای
 *   • یازده بازه زمانی، از یک دقیقه تا یک هفته
 *   • روی نمودار: MA، EMA، بولینگر، VWAP، ایچیموکو، سهمی سار
 *   • پنل جداگانه: RSI، MACD، استوکاستیک، ATR، OBV، CCI، ویلیامز، MFI
 *   • دوازده ابزار ترسیم + آهن‌ربا، قفل، پنهان‌سازی، برگرد و جلو برو
 *   • ترسیم‌ها **روی حساب کاربر ذخیره می‌شوند** و با ورود دوباره برمی‌گردند
 *   • مقیاس عادی/لگاریتمی/درصدی، معکوس‌کردن محور، عکس‌گرفتن از نمودار
 *   • شمارش معکوس بسته‌شدن کندل، سفارش‌های باز کاربر روی نمودار
 *   • پنج نما: نمودار، عمق بازار، نقشه بازار، نمودار جهانی، نمودار پیشرفته
 *
 * ── چرا تریدینگ‌ویو جاسازی نشد ──────────────────────────────────────────
 * ویجت **رایگانِ** تریدینگ‌ویو قیمت را از سرورهای خودش می‌گیرد، نه از ما.
 * قیمت این سایت تومانی است و حاشیه سود خودش را هم دارد؛ یعنی نمودار عددی
 * نشان می‌داد که با دکمه «خرید» همان صفحه **نمی‌خواند**. برای صرافی،
 * اختلاف نمودار و قیمت اجرا یعنی شکایت کاربر.
 *
 * ولی راه درستش هم گذاشته شده: اگر **کتابخانه نموداری** تریدینگ‌ویو روی
 * سایت نصب شود (همان کاری که نوبیتکس کرده)، زبانه «نمودار پیشرفته» خودش
 * ظاهر می‌شود و روی داده خودمان کار می‌کند. راهنما در
 * `docs/tradingview.md`.
 *
 * ریاضیات اندیکاتورها عمداً اینجا نیست — در `lib/indicators.ts` است و
 * جداگانه با مرجع عددی آزموده می‌شود.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createChart, ColorType, IChartApi, ISeriesApi } from 'lightweight-charts'
import {
  Maximize2, Minimize2, Crosshair, Settings2, RotateCcw, Check,
  MousePointer2, Minus, TrendingUp, MoveRight, Square, AlignJustify, Type,
  Trash2, Eraser, CandlestickChart, AreaChart, LayoutGrid, Globe, Lock,
  Camera, Magnet, Eye, EyeOff, Undo2, Redo2, PenLine, MoveDiagonal,
  MoveVertical, Ruler, Signature, Unlock, Gauge, BarChart3, Flag,
} from 'lucide-react'
import { get, put } from '../lib/api'
import { useMediaQuery } from '../lib/media'
import { clsx, compact as compactNum, faChartTick, faStamp, num, pct, parseDate } from '../lib/format'
import { useAuth, useMarket } from '../store'
import { Popover } from './Popover'
import { DrawLayer, Drawing, Tool, sanitizeDrawings } from './DrawLayer'
import { DepthChart } from './DepthChart'
import { MarketMap } from './MarketMap'
import { IndicatorPane, PaneKey, PANE_META } from './IndicatorPane'
import { nearestBarIndex } from '../lib/chartCoords'
import { VolumeProfileLayer } from './VolumeProfileLayer'
import { technicalSummary } from '../lib/signals'
import { TradingViewChart, tvAvailable } from './TradingViewChart'
import {
  Candle, bollinger, ema, heikinAshi, ichimoku, psar, sma, toLine, vwap,
} from '../lib/indicators'

/*
 * این ثابت‌ها معادل عددی enum های خود کتابخانه‌اند
 * (`LineStyle.Dashed`، `PriceScaleMode.*`، `LineType.WithSteps`).
 * عمداً به‌جای import از خود enum، عدد نوشته شده تا اگر نام صادرشده در
 * نسخه‌های مختلف کتابخانه فرق کند، بیلد نشکند.
 */
const DASHED = 2
const LOG_SCALE = 1
const PCT_SCALE = 2
const STEP_LINE = 1

const TFS = [
  { key: '1m', label: '۱ دقیقه', short: '۱د', sec: 60 },
  { key: '5m', label: '۵ دقیقه', short: '۵د', sec: 300 },
  { key: '15m', label: '۱۵ دقیقه', short: '۱۵د', sec: 900 },
  { key: '30m', label: '۳۰ دقیقه', short: '۳۰د', sec: 1800 },
  { key: '1h', label: '۱ ساعت', short: '۱س', sec: 3600 },
  { key: '2h', label: '۲ ساعت', short: '۲س', sec: 7200 },
  { key: '4h', label: '۴ ساعت', short: '۴س', sec: 14400 },
  { key: '6h', label: '۶ ساعت', short: '۶س', sec: 21600 },
  { key: '12h', label: '۱۲ ساعت', short: '۱۲س', sec: 43200 },
  { key: '1d', label: '۱ روز', short: '۱ر', sec: 86400 },
  { key: '1w', label: '۱ هفته', short: '۱ه', sec: 604800 },
]

const TYPES = [
  { key: 'candle', label: 'شمعی' },
  { key: 'hollow', label: 'شمع توخالی' },
  { key: 'heikin', label: 'هایکن آشی' },
  { key: 'bar', label: 'میله‌ای' },
  { key: 'line', label: 'خطی' },
  { key: 'area', label: 'سطحی' },
  { key: 'baseline', label: 'خط پایه' },
  { key: 'step', label: 'پله‌ای' },
] as const
type ChartType = (typeof TYPES)[number]['key']

type Overlay = 'ma' | 'ema' | 'bb' | 'vwap' | 'ichimoku' | 'psar'

const OVERLAYS: { key: Overlay; label: string; hint: string }[] = [
  { key: 'ma', label: 'میانگین ساده (۷ / ۲۵ / ۹۹)', hint: 'روند کوتاه، میان و بلندمدت' },
  { key: 'ema', label: 'میانگین نمایی (۱۲ / ۲۶)', hint: 'واکنش سریع‌تر به قیمت تازه' },
  { key: 'bb', label: 'باندهای بولینگر (۲۰، ۲σ)', hint: 'نوسان و اشباع' },
  { key: 'vwap', label: 'میانگین وزنیِ حجم', hint: 'قیمت میانگین واقعی معاملات' },
  { key: 'ichimoku', label: 'ایچیموکو', hint: 'تنکان، کیجون، دو مرز ابر و خط تأخیری' },
  { key: 'psar', label: 'سهمی سار', hint: 'نقطه‌چین برگشت روند' },
]

const PANES: PaneKey[] = ['rsi', 'macd', 'stoch', 'atr', 'obv', 'cci', 'wr', 'mfi']

const TOOLS: { key: Tool; label: string; Icon: any }[] = [
  { key: 'none', label: 'انتخاب و جابه‌جایی', Icon: MousePointer2 },
  { key: 'hline', label: 'خط افقی (حمایت / مقاومت)', Icon: Minus },
  { key: 'vline', label: 'خط عمودی (زمان مهم)', Icon: MoveVertical },
  { key: 'trend', label: 'خط روند', Icon: TrendingUp },
  { key: 'ray', label: 'نیم‌خط (ادامه‌دار)', Icon: MoveRight },
  { key: 'ext', label: 'خط بی‌نهایت', Icon: MoveDiagonal },
  { key: 'channel', label: 'کانال موازی (سه نقطه)', Icon: PenLine },
  { key: 'rect', label: 'ناحیه', Icon: Square },
  { key: 'fib', label: 'اصلاح فیبوناچی', Icon: AlignJustify },
  { key: 'arrow', label: 'پیکان', Icon: MoveRight },
  { key: 'brush', label: 'قلم آزاد', Icon: Signature },
  { key: 'measure', label: 'خط‌کش (اختلاف قیمت و تعداد کندل)', Icon: Ruler },
  { key: 'text', label: 'یادداشت', Icon: Type },
]

const SCALES = [
  { key: 'normal', label: 'عادی', mode: 0 },
  { key: 'log', label: 'لگاریتمی', mode: LOG_SCALE },
  { key: 'pct', label: 'درصدی', mode: PCT_SCALE },
] as const
type ScaleKey = (typeof SCALES)[number]['key']

// عرض کمینه محور قیمت؛ بدون آن، محورِ باریکِ RSI باعث می‌شد ناحیه رسمِ
// پنل با نمودار اصلی هم‌عرض نباشد و خط‌ها چند پیکسل جابه‌جا دیده شوند.
const PANE_SCALE_W = 64

const DRAW_COLORS = ['#F5C24C', '#00E39A', '#FF4D6D', '#37D0FF', '#7C5CFF', '#FFFFFF']

const C = {
  up: '#00E39A', down: '#FF4D6D', grid: 'rgba(255,255,255,.04)',
  border: 'rgba(255,255,255,.07)', text: '#8B93AD',
  ma7: '#F5C24C', ma25: '#7C5CFF', ma99: '#37D0FF',
  ema12: '#FF8A5B', ema26: '#8AF57A', bb: 'rgba(124,92,255,.55)', vwap: '#FF6FD8',
  ichT: '#37D0FF', ichK: '#FF6FD8', ichA: 'rgba(0,227,154,.75)',
  ichB: 'rgba(255,77,109,.75)', ichC: '#8B93AD', psar: '#F5C24C',
}

/** خط‌های اندیکاتور روی نمودار: شناسه، رنگ و ضخامت هر کدام یک‌جا */
const OVERLAY_LINES: {
  id: string; group: Overlay; color: string; width?: number; dashed?: boolean; dots?: boolean
}[] = [
  { id: 'ma7', group: 'ma', color: C.ma7 },
  { id: 'ma25', group: 'ma', color: C.ma25 },
  { id: 'ma99', group: 'ma', color: C.ma99 },
  { id: 'ema12', group: 'ema', color: C.ema12 },
  { id: 'ema26', group: 'ema', color: C.ema26 },
  { id: 'bbU', group: 'bb', color: C.bb, dashed: true },
  { id: 'bbM', group: 'bb', color: C.bb },
  { id: 'bbL', group: 'bb', color: C.bb, dashed: true },
  { id: 'vwap', group: 'vwap', color: C.vwap, width: 2 },
  { id: 'ichT', group: 'ichimoku', color: C.ichT },
  { id: 'ichK', group: 'ichimoku', color: C.ichK },
  { id: 'ichA', group: 'ichimoku', color: C.ichA },
  { id: 'ichB', group: 'ichimoku', color: C.ichB },
  { id: 'ichC', group: 'ichimoku', color: C.ichC, dashed: true },
  { id: 'psar', group: 'psar', color: C.psar, dots: true },
]

const baseOptions = (h: number) => ({
  layout: {
    background: { type: ColorType.Solid, color: 'transparent' },
    textColor: C.text, fontFamily: 'Vazirmatn, sans-serif', attributionLogo: false,
  },
  grid: { vertLines: { color: C.grid }, horzLines: { color: C.grid } },
  rightPriceScale: { borderColor: C.border, minimumWidth: PANE_SCALE_W },
  timeScale: {
    borderColor: C.border, timeVisible: true, secondsVisible: false,
    // برچسب‌های محور به تقویم **شمسی**. نمودارهای مبتنی بر تریدینگ‌ویو —
    // از جمله نوبیتکس — محور زمانشان میلادی است؛ کاربر ایرانی «۲۷ مرداد»
    // را می‌فهمد، «Aug 18» را باید در ذهنش تبدیل کند.
    tickMarkFormatter: (t: any, kind: number) => faChartTick(Number(t), kind),
  },
  crosshair: {
    vertLine: { color: 'rgba(124,92,255,.5)', labelBackgroundColor: '#6438F5' },
    horzLine: { color: 'rgba(124,92,255,.5)', labelBackgroundColor: '#6438F5' },
  },
  // جابه‌جایی «پرتابی» روی لمس: انگشت را رها می‌کنی و نمودار نرم می‌ایستد.
  // بدون این، حرکت روی گوشی خشک و پله‌ای حس می‌شود.
  kineticScroll: { touch: true, mouse: false },
  handleScale: { pinch: true, axisPressedMouseMove: true, mouseWheel: true },
  localization: {
    locale: 'fa-IR',
    timeFormatter: (t: any) => faStamp(Number(t)),
  },
  height: h,
  autoSize: true,
})

/* ══════════════════════════════════════════════════ پوسته: نماها */

const TABS = [
  { key: 'chart', label: 'نمودار', Icon: CandlestickChart },
  { key: 'depth', label: 'عمق بازار', Icon: AreaChart },
  { key: 'map', label: 'نقشه بازار', Icon: LayoutGrid },
  { key: 'global', label: 'نمودار جهانی', Icon: Globe },
] as const
type TabKey = (typeof TABS)[number]['key'] | 'tv'

export function ProChart({ symbol, quote }: { symbol: string; quote: string }) {
  const [tab, setTab] = useState<TabKey>('chart')
  const [full, setFull] = useState(false)
  const [tv, setTv] = useState(false)
  // روی گوشی، «تمام‌صفحه» باید واقعاً تمام صفحه باشد: حاشیه ۸ پیکسلی و
  // گوشه‌های گرد، روی صفحه ۳۶۰ پیکسلی یعنی از دست دادن جای واقعیِ نمودار.
  const touch = useMediaQuery('(pointer: coarse)')
  // ارجاع ثابت: بدون آن، هر رندرِ پوسته ویجت تریدینگ‌ویو را می‌بست و از نو
  // می‌ساخت و چیدمان و اندیکاتورهای کاربر می‌پرید.
  const tvFail = useCallback(() => setTv(false), [])

  const base = symbol.replace(/(IRT|USDT)$/, '')
  const globalSymbol = `${base}USDT`

  // زبانه «نمودار پیشرفته» فقط وقتی دیده می‌شود که کتابخانه‌اش روی سرور
  // باشد. اگر نباشد، کاربر نباید دکمه‌ای ببیند که به پیام خطا می‌رسد.
  useEffect(() => { tvAvailable().then(setTv) }, [])

  // وقتی ابزار ترسیم فعال است، Esc یعنی «این خط را بی‌خیال»، نه «از
  // تمام‌صفحه بیا بیرون». هر دو شنونده روی پنجره‌اند و در یک رویداد صدا
  // زده می‌شوند، پس این پرچم تصمیم می‌گیرد کدامشان کار کند.
  const drawing = useRef(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !drawing.current) setFull(false)
    }
    if (full) window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [full])

  const tabs = tv
    ? [...TABS, { key: 'tv' as const, label: 'نمودار پیشرفته', Icon: TrendingUp }]
    : TABS

  return (
    // `pt-safe` فقط در حالت تمام‌صفحه‌ی لمسی لازم است: آنجا کارت تا لبه‌ی
    // بالای صفحه می‌رود و در اپِ نصب‌شده روی آیفون، ردیف نماها زیر ناچ
    // گم می‌شود — همان اتفاقی که برای هدر سایت افتاد.
    <div className={clsx('card flex flex-col overflow-hidden p-0',
      full
        ? clsx('fixed z-[800] h-auto', touch ? 'inset-0 !rounded-none pt-safe' : 'inset-2')
        : 'h-full')}>
      {/* ── نماها */}
      <div className="scroll-x flex shrink-0 items-center gap-1 border-b border-white/[.06] px-2 pt-2">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key as TabKey)}
            className={clsx('flex items-center gap-1.5 whitespace-nowrap rounded-t-lg px-3 py-1.5 text-xs font-medium transition',
              tab === t.key
                ? 'bg-white/[.07] text-white shadow-[inset_0_-2px_0_0_theme(colors.brand.500)]'
                : 'text-slate-500 hover:bg-white/[.04] hover:text-slate-300')}>
            <t.Icon className="h-3.5 w-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        {tab === 'chart' && (
          <ChartCore key={symbol} symbol={symbol} quote={quote}
            full={full} setFull={setFull} drawing={drawing} />
        )}
        {tab === 'global' && (
          <ChartCore key={globalSymbol} symbol={globalSymbol} quote="USDT"
            full={full} setFull={setFull} drawing={drawing}
            note="بازار جهانی همین ارز بر حسب تتر" />
        )}
        {tab === 'depth' && <DepthChart symbol={symbol} quote={quote} />}
        {tab === 'map' && <MarketMap quote={quote} />}
        {tab === 'tv' && <TradingViewChart symbol={symbol} onFail={tvFail} />}
      </div>
    </div>
  )
}

/* ══════════════════════════════════════════════════ خودِ نمودار */

type CoreProps = {
  symbol: string
  quote: string
  full: boolean
  setFull: (v: boolean | ((p: boolean) => boolean)) => void
  /** به پوسته خبر می‌دهد که ابزار ترسیم فعال است (برای رفتار درستِ Esc) */
  drawing: React.MutableRefObject<boolean>
  note?: string
}

function ChartCore({ symbol, quote, full, setFull, drawing, note }: CoreProps) {
  const { user } = useAuth()
  const rootBox = useRef<HTMLDivElement>(null)
  const mainBox = useRef<HTMLDivElement>(null)
  const settingsBtn = useRef<HTMLButtonElement>(null)

  const main = useRef<IChartApi | null>(null)
  const priceSeries = useRef<ISeriesApi<any> | null>(null)
  const volSeries = useRef<ISeriesApi<any> | null>(null)
  const overlayRefs = useRef<Record<string, ISeriesApi<any>>>({})
  const orderLines = useRef<any[]>([])

  // لایه ترسیم به **خودِ شیء** نمودار و سری نیاز دارد، نه به ref؛ پس در
  // state هم نگه داشته می‌شوند تا با ساخته‌شدنشان یک رندر تازه رخ بدهد.
  const [chartApi, setChartApi] = useState<IChartApi | null>(null)
  const [seriesApi, setSeriesApi] = useState<ISeriesApi<any> | null>(null)

  const prefs = useChartPrefs()
  const { tf, type, overlays, panes, scale, invert } = prefs

  const [menu, setMenu] = useState(false)
  const [candles, setCandles] = useState<Candle[]>([])
  const [hover, setHover] = useState<Candle | null>(null)
  const [hoverTime, setHoverTime] = useState<number | null>(null)
  const [err, setErr] = useState('')
  const [partial, setPartial] = useState(false)
  const [signalOpen, setSignalOpen] = useState(false)
  const signalBtn = useRef<HTMLButtonElement>(null)
  const [box, setBox] = useState({ w: 900, h: 420 })
  const [chrome, setChrome] = useState(150)
  const [scaleW, setScaleW] = useState(PANE_SCALE_W)
  const toolsRef = useRef<HTMLDivElement>(null)
  const drawRef = useRef<HTMLDivElement>(null)
  const footRef = useRef<HTMLDivElement>(null)
  const [left, setLeft] = useState('')

  const hasData = candles.length > 0
  const on = (k: Overlay) => overlays.includes(k)
  const paneOn = (k: PaneKey) => panes.includes(k)
  const compact = box.w < 560
  // انگشت حدود ۹ میلی‌متر است؛ دکمه ۲۸ پیکسلی روی گوشی یعنی خطای مکرر.
  // روی ماوس همان اندازه جمع‌وجور می‌ماند تا نوار ابزار شلوغ نشود.
  const touch = useMediaQuery('(pointer: coarse)')
  const btn = touch ? 'h-9 min-w-9 px-2.5' : 'px-2 py-1'
  const iconBtn = touch ? 'h-9 w-9' : 'h-7 w-7'
  // با تغییر تعداد اندیکاتورها، برچسب دکمه پهن‌تر می‌شود و نوار ابزار ممکن
  // است یک ردیف بشکند؛ پس در وابستگی‌های اندازه‌گیری هم می‌آید.
  const activeCount = overlays.length + panes.length

  /* ─────────────────────────────────────────── اندازه ظرف
   *
   * چیدمان نمودار باید خودش را با جای موجود جور کند، نه برعکس. روی گوشی
   * کارت حدود ۴۴۰ پیکسل ارتفاع دارد؛ اگر نوار ابزار سه ردیف شود و دو پنل
   * ثابتِ ۱۱۰ پیکسلی هم زیرش بنشیند، جا برای خودِ نمودار نمی‌ماند و کاربر
   * یک کارتِ به‌هم‌ریخته می‌بیند. پس ارتفاع پنل‌ها از روی همین اندازه حساب
   * می‌شود و نوار ابزار در حالت باریک، فشرده می‌شود.
   */
  const measure = useCallback(() => {
    const b = rootBox.current?.getBoundingClientRect()
    if (b && b.width > 0) setBox({ w: b.width, h: b.height })
    setChrome((toolsRef.current?.offsetHeight ?? 0)
      + (drawRef.current?.offsetHeight ?? 0)
      + (footRef.current?.offsetHeight ?? 0))
  }, [])

  useEffect(() => {
    if (!rootBox.current) return
    const ro = new ResizeObserver(measure)
    ro.observe(rootBox.current)
    return () => ro.disconnect()
  }, [measure])

  // اندازه‌گیری دوباره برای تغییرهایی که **ظرف را بزرگ نمی‌کنند**.
  //
  // باز کردن نوار ترسیم یک ردیف ۴۰ پیکسلی اضافه می‌کند، ولی چون خودِ کارت
  // ارتفاع ثابت دارد، `ResizeObserver` هیچ رویدادی نمی‌دهد و اندازه‌ی
  // قبلی کهنه می‌ماند — نتیجه‌اش دقیقاً همان سرریزی بود که می‌خواستیم
  // برطرف کنیم، این بار پاورقی و هشدارهایش را می‌بلعید.
  // `useLayoutEffect` است تا حتی یک فریم با اندازه غلط رسم نشود.
  useLayoutEffect(measure, [measure, prefs.drawBar, compact, activeCount, panes.length])

  /*
   * ارتفاع پنل‌ها از **جای واقعیِ باقی‌مانده** حساب می‌شود، نه از یک درصدِ
   * حدسی.
   *
   * روی گوشی، کارت ۴۴۰ پیکسل است و نوار نماها و ابزار و پاورقی حدود ۱۵۰ تا
   * ۱۹۰ پیکسلش را می‌برند. با فرمول درصدی، سه پنلِ باز از کارت بیرون می‌زد
   * و چون کارت `overflow-hidden` است، پاورقی و پنل آخر **کاملاً ناپیدا**
   * می‌شدند. حالا هرچه جا نشود اصلاً باز نمی‌شود و به کاربر هم گفته می‌شود.
   */
  const MIN_CHART = 130
  const avail = Math.max(0, box.h - chrome - MIN_CHART)
  const fitPanes = Math.max(0, Math.floor(avail / 58))
  const shownPanes = panes.slice(0, fitPanes)
  const hiddenPanes = panes.length - shownPanes.length
  const paneH = shownPanes.length
    ? Math.max(40, Math.min(110, Math.floor(avail / shownPanes.length) - 18))
    : 0

  /*
   * عرض محور قیمتِ پنل‌ها باید با نمودار اصلی یکی باشد، وگرنه ناحیه رسم
   * پهن‌تر می‌شود و خط‌ها چند پیکسل نسبت به کندل‌ها می‌لغزند. `minimumWidth`
   * فقط کف است؛ پس عرض واقعیِ محور اصلی اندازه‌گیری و همان به پنل‌ها داده
   * می‌شود. با عوض‌شدن قیمت‌ها (و پهن‌تر شدن برچسب‌ها) دوباره حساب می‌شود.
   */
  useEffect(() => {
    if (!chartApi || !box.w) return
    try {
      const pane = (chartApi as any).paneSize?.()
      const w = pane?.width ? Math.round(box.w - pane.width) : 0
      if (w > 20 && w < 240) setScaleW(w)
    } catch { /* بی‌اهمیت */ }
  }, [chartApi, box.w, candles])

  /* ─────────────────────────────────────────────── ساخت نمودار اصلی */
  useEffect(() => {
    if (!mainBox.current) return
    const chart = createChart(mainBox.current, baseOptions(320) as any)
    main.current = chart
    setChartApi(chart)
    const vol = chart.addHistogramSeries({ priceFormat: { type: 'volume' }, priceScaleId: 'vol' })
    chart.priceScale('vol').applyOptions({ scaleMargins: { top: 0.84, bottom: 0 } })
    volSeries.current = vol

    // فقط «زمانِ زیر نشانگر» نگه داشته می‌شود، نه خودِ کندل.
    // اگر کندل را اینجا ذخیره می‌کردیم، با هر بار تازه‌شدن داده (هر ۱۵
    // ثانیه) عددِ روی صفحه کهنه می‌ماند تا کاربر ماوس را تکان بدهد.
    chart.subscribeCrosshairMove((p: any) => {
      setHoverTime(p?.time == null ? null : Number(p.time))
    })
    return () => {
      chart.remove()
      main.current = null; priceSeries.current = null
      setChartApi(null); setSeriesApi(null)
    }
  }, [])

  useEffect(() => {
    setHover(hoverTime == null ? null : candles.find((c) => c.time === hoverTime) ?? null)
  }, [hoverTime, candles])

  /* ─────────────────────────────────────────── نوع نمودار (شمعی/خطی/…) */
  // عمداً فقط به `type` وابسته است.
  //
  // اگر `candles` هم در وابستگی‌ها بود، هر ۱۵ ثانیه که داده تازه می‌رسید
  // سریِ قیمت حذف و از نو ساخته می‌شد: نمودار پلک می‌زد، بزرگ‌نماییِ کاربر
  // می‌پرید، و خط‌های سفارشِ باز پاک می‌شدند. ریختن داده کارِ افکت بعدی است.
  useEffect(() => {
    const chart = main.current
    if (!chart) return
    if (priceSeries.current) { chart.removeSeries(priceSeries.current); priceSeries.current = null }
    let s: ISeriesApi<any>
    if (type === 'candle' || type === 'heikin') {
      s = chart.addCandlestickSeries({
        upColor: C.up, downColor: C.down, borderVisible: false,
        wickUpColor: C.up, wickDownColor: C.down,
      })
    } else if (type === 'hollow') {
      // شمع توخالی: بدنه صعودی خالی، بدنه نزولی پر
      s = chart.addCandlestickSeries({
        upColor: 'rgba(0,0,0,0)', downColor: C.down, borderVisible: true,
        borderUpColor: C.up, borderDownColor: C.down,
        wickUpColor: C.up, wickDownColor: C.down,
      })
    } else if (type === 'bar') {
      s = chart.addBarSeries({ upColor: C.up, downColor: C.down })
    } else if (type === 'line') {
      s = chart.addLineSeries({ color: C.ma25, lineWidth: 2 })
    } else if (type === 'step') {
      s = chart.addLineSeries({ color: C.ma25, lineWidth: 2, lineType: STEP_LINE } as any)
    } else if (type === 'baseline') {
      s = chart.addBaselineSeries({
        topLineColor: C.up, topFillColor1: 'rgba(0,227,154,.28)', topFillColor2: 'rgba(0,227,154,0)',
        bottomLineColor: C.down, bottomFillColor1: 'rgba(255,77,109,.28)',
        bottomFillColor2: 'rgba(255,77,109,0)', lineWidth: 2,
      } as any)
    } else {
      s = chart.addAreaSeries({
        lineColor: C.ma25, topColor: 'rgba(124,92,255,.35)', bottomColor: 'rgba(124,92,255,0)',
        lineWidth: 2,
      })
    }
    priceSeries.current = s
    setSeriesApi(s)
    if (candles.length) applyPrice(s, candles, type)
  }, [type])

  // ریختن داده تازه در سریِ موجود — بدون ساختن دوباره
  useEffect(() => {
    if (priceSeries.current && candles.length) applyPrice(priceSeries.current, candles, type)
  }, [candles, type])

  /* ─────────────────────────────────────────── حالت محور قیمت */
  useEffect(() => {
    const mode = SCALES.find((s) => s.key === scale)?.mode ?? 0
    main.current?.priceScale('right').applyOptions({ mode, invertScale: invert } as any)
  }, [scale, invert])

  /* ─────────────────────────────────────────── گرفتن داده */
  useEffect(() => {
    let alive = true
    const load = async () => {
      try {
        const r = await get<{ data: any[]; partial?: boolean }>(
          `/market/ohlcv/${symbol}?tf=${tf}&limit=500`)
        if (!alive) return
        const rows: Candle[] = (r.data || []).map((c) => ({
          time: c.time, open: c.open, high: c.high, low: c.low, close: c.close,
          volume: c.volume ?? 0,
        }))
        setCandles(rows)
        // «ناقص» یعنی تاریخچه این بازه هنوز به اندازه درخواست‌شده نرسیده —
        // نه اینکه چیزی ساخته شده باشد. پیش از این، جای خالیِ تاریخچه با
        // داده تصادفی پر می‌شد و همان بود که نمودار را صاف و بی‌حرکت
        // نشان می‌داد.
        setPartial(!!r.partial && rows.length > 0)
        setErr(rows.length ? '' : 'داده‌ای برای این بازه نیست')
        volSeries.current?.setData(rows.map((c) => ({
          time: c.time, value: c.volume,
          color: c.close >= c.open ? 'rgba(0,227,154,.30)' : 'rgba(255,77,109,.30)',
        })) as any)
      } catch (e: any) {
        if (alive) setErr(e?.message || 'دریافت نمودار ناموفق بود')
      }
    }
    load()
    const id = setInterval(load, 15000)
    return () => { alive = false; clearInterval(id) }
  }, [symbol, tf])

  /* ─────────────────────────────────────────── شمارش معکوس بسته‌شدن کندل */
  useEffect(() => {
    const sec = TFS.find((t) => t.key === tf)?.sec ?? 3600
    const tick = () => {
      const last = candles.length ? candles[candles.length - 1].time : 0
      if (!last) { setLeft(''); return }
      const end = last + sec
      const d = Math.max(0, end - Math.floor(Date.now() / 1000))
      const hh = Math.floor(d / 3600)
      const mm = Math.floor((d % 3600) / 60)
      const ss = d % 60
      const two = (v: number) => String(v).padStart(2, '0')
      setLeft(hh ? `${two(hh)}:${two(mm)}:${two(ss)}` : `${two(mm)}:${two(ss)}`)
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [candles, tf])

  /* ─────────────────────────────────────────── اندیکاتورهای روی نمودار */
  //
  // مثل سریِ قیمت، اینجا هم **ساخت** از **ریختن داده** جدا شده است.
  //
  // قبلاً هر دو در یک افکت با وابستگی `[candles, overlays]` بودند؛ یعنی هر
  // ۱۵ ثانیه که داده تازه می‌رسید، هر خط اندیکاتور حذف و از نو ساخته
  // می‌شد — همان پلک‌زدنی که برای سریِ قیمت با دقت از آن پرهیز شده بود.
  useEffect(() => {
    const chart = main.current
    if (!chart) return
    for (const o of OVERLAY_LINES) {
      if (!overlays.includes(o.group)) continue
      overlayRefs.current[o.id] = chart.addLineSeries({
        color: o.color, lineWidth: (o.width ?? 1) as any,
        priceLineVisible: false, lastValueVisible: false,
        ...(o.dashed ? { lineStyle: DASHED } : {}),
        // سهمی سار نقطه است نه خط؛ اگر نسخه کتابخانه این گزینه را نشناسد،
        // بدترین حالت یک خط نازک می‌شود، نه خطا.
        ...(o.dots ? { lineVisible: false, pointMarkersVisible: true } : {}),
      } as any)
    }
    return () => {
      for (const k of Object.keys(overlayRefs.current)) {
        // اگر خودِ نمودار پیش‌تر بسته شده باشد، سری‌هایش هم رفته‌اند
        try { chart.removeSeries(overlayRefs.current[k]) } catch { /* بسته شده */ }
        delete overlayRefs.current[k]
      }
    }
    // `type` هم لازم است: با عوض‌شدن نوع نمودار، سریِ قیمت از نو ساخته
    // می‌شود و چون کتابخانه به ترتیب ساخت رسم می‌کند، اندیکاتورهای قدیمی
    // زیر شمع‌ها پنهان می‌ماندند.
  }, [overlays, type])

  // ریختن داده در همان سری‌های موجود
  useEffect(() => {
    if (!candles.length) return
    const closes = candles.map((c) => c.close)
    const data: Record<string, any[]> = {}
    if (on('ma')) {
      data.ma7 = toLine(candles, sma(closes, 7))
      data.ma25 = toLine(candles, sma(closes, 25))
      data.ma99 = toLine(candles, sma(closes, 99))
    }
    if (on('ema')) {
      data.ema12 = toLine(candles, ema(closes, 12))
      data.ema26 = toLine(candles, ema(closes, 26))
    }
    if (on('bb')) {
      const b = bollinger(closes, 20, 2)
      data.bbU = toLine(candles, b.upper)
      data.bbM = toLine(candles, b.middle)
      data.bbL = toLine(candles, b.lower)
    }
    if (on('vwap')) data.vwap = toLine(candles, vwap(candles))
    if (on('ichimoku')) {
      const i = ichimoku(candles)
      data.ichT = toLine(candles, i.tenkan)
      data.ichK = toLine(candles, i.kijun)
      data.ichA = toLine(candles, i.spanA)
      data.ichB = toLine(candles, i.spanB)
      data.ichC = toLine(candles, i.chikou)
    }
    if (on('psar')) data.psar = toLine(candles, psar(candles))
    for (const [id, rows] of Object.entries(data)) {
      try { overlayRefs.current[id]?.setData(rows as any) } catch { /* سری رفته */ }
    }
  }, [candles, overlays, type])

  /* ─────────────────────────────────────────── سفارش‌های باز کاربر */
  //
  // خط‌های قیمت به **همان سری**ای بسته‌اند که رویش ساخته شده‌اند. اگر نوع
  // نمودار عوض شود سری قبلی حذف شده و خط‌هایش هم با آن رفته‌اند؛ پس مرجع
  // سری در یک متغیر محلی گرفته می‌شود، نه از روی ref که ممکن است تا زمان
  // پاک‌سازی عوض شده باشد.
  useEffect(() => {
    const series = priceSeries.current
    if (!series || !user) return
    let alive = true
    const lines: any[] = []
    get(`/orders?status=open&limit=50`).then((r: any) => {
      if (!alive) return
      const rows: any[] = Array.isArray(r) ? r : (r?.data ?? [])
      for (const o of rows) {
        if (String(o.symbol) !== symbol || !o.price) continue
        const buy = String(o.side).toLowerCase().startsWith('b')
        try {
          lines.push(series.createPriceLine({
            price: Number(o.price),
            color: buy ? C.up : C.down,
            lineWidth: 1, lineStyle: DASHED, axisLabelVisible: true,
            title: buy ? 'خرید من' : 'فروش من',
          } as any))
        } catch { /* سری دیگر زنده نیست */ }
      }
      orderLines.current = lines
    }).catch(() => { /* نمایش سفارش‌ها اختیاری است؛ نباید نمودار را بشکند */ })
    return () => {
      alive = false
      for (const l of lines) { try { series.removePriceLine(l) } catch { /* حذف‌شده */ } }
      orderLines.current = []
    }
  }, [user, symbol, type, hasData])

  /* ─────────────────────────────────────────── معاملات خودِ کاربر روی نمودار
   *
   * سفارش‌های **باز** از قبل با خط‌چین نشان داده می‌شدند؛ این یکی معامله‌های
   * **انجام‌شده** است: دقیقاً کجای نمودار خریدید و کجا فروختید.
   *
   * نکته فنی: نشانه‌ها فقط روی زمانی می‌نشینند که کندلی با همان زمان وجود
   * داشته باشد؛ پس زمان هر معامله به سطلِ همان بازه گرد می‌شود، وگرنه
   * کتابخانه بی‌صدا دورشان می‌ریزد.
   */
  const [myTrades, setMyTrades] = useState<any[]>([])

  // گرفتن معامله‌ها فقط با عوض‌شدن بازار یا کاربر — نه هر ۱۵ ثانیه که کندل
  // تازه می‌رسد. جدا کردن «گرفتن داده» از «کشیدن نشانه» همین را ممکن کرد.
  useEffect(() => {
    if (!user || !prefs.myTrades) { setMyTrades([]); return }
    let alive = true
    get(`/orders/trades?symbol=${symbol}&limit=100`).then((r: any) => {
      if (!alive) return
      setMyTrades(Array.isArray(r) ? r : (r?.data ?? []))
    }).catch(() => { /* نمایش معاملات اختیاری است */ })
    return () => { alive = false }
  }, [user, symbol, prefs.myTrades])

  useEffect(() => {
    const series = priceSeries.current
    if (!series) return
    if (!myTrades.length || !candles.length) {
      try { (series as any).setMarkers?.([]) } catch { /* سری رفته */ }
      return
    }
    const times = candles.map((c) => c.time)
    const firstT = times[0]
    const lastT = times[times.length - 1]
    // حاشیه مجاز = یک کندل، نه یک روز.
    //
    // روی بازه یک‌دقیقه‌ای، ۵۰۰ کندل حدود هشت ساعت است؛ با حاشیه یک‌روزه،
    // همه معامله‌های بیست‌وچهار ساعت قبل هم رد می‌شدند و بعد روی
    // **چپ‌ترین کندل** تلنبار می‌شدند — نشانه‌ای که ساعت‌ها با واقعیت فاصله
    // داشت.
    const barSec = TFS.find((t) => t.key === tf)?.sec ?? 3600
    // زمان معامله را به **کندلِ واقعی** می‌چسبانیم، نه به سطلِ حساب‌شده.
    //
    // حساب‌کردن سطل با `floor(ms/sec)*sec` فرض می‌کند مرز کندل‌ها روی مبدأ
    // یونیکس است؛ برای بازه هفتگی یعنی «پنجشنبه ۰۰:۰۰ گرینویچ» که هیچ ربطی
    // به کندل‌های واقعی ندارد. نشانه‌ای که روی زمانی بیفتد که کندل ندارد،
    // بی‌صدا دور ریخته می‌شود.
    const agg = new Map<string, { time: number; buy: boolean; amount: number; n: number }>()
    for (const t of myTrades) {
      const d = parseDate(String(t.createdAt || ''))
      if (!d) continue
      const sec = Math.floor(d.getTime() / 1000)
      if (sec < firstT - barSec || sec > lastT + barSec) continue
      const idx = nearestBarIndex(times, sec)
      if (idx < 0) continue
      const bar = times[idx]
      if (bar < firstT || bar > lastT) continue
      const buy = String(t.side).toLowerCase().startsWith('b')
      const key = `${bar}-${buy ? 'b' : 's'}`
      const cur = agg.get(key)
      const amount = Number(t.amount) || 0
      if (cur) { cur.amount += amount; cur.n += 1 }
      else agg.set(key, { time: bar, buy, amount, n: 1 })
    }
    const marks = [...agg.values()]
      .sort((a, b) => a.time - b.time)
      .map((m) => ({
        time: m.time,
        position: m.buy ? 'belowBar' : 'aboveBar',
        color: m.buy ? C.up : C.down,
        shape: m.buy ? 'arrowUp' : 'arrowDown',
        text: `${m.buy ? 'خرید' : 'فروش'} ${compactNum(m.amount)}${m.n > 1 ? ` (${m.n})` : ''}`,
      }))
    try { (series as any).setMarkers?.(marks) } catch { /* سری رفته */ }
  }, [myTrades, candles, tf, type])

  /* ─────────────────────────────────────────── نرخ برابری با بازار تتری
   *
   * ⚠ این عدد را عمداً «اختلاف با بازار جهانی» **نام نگذاشتیم**.
   *
   * هر دو طرفِ کسر از قیمت‌های خودِ همین سایت می‌آید (قیمت تومانی، و قیمت
   * تتری ضرب‌در نرخ تتر). پس چیزی که نشان می‌دهد اختلاف با بازار جهانی
   * نیست؛ **ناسازگاری درونیِ دو بازار خودمان** است — عددی که در حالت سالم
   * باید نزدیک صفر بماند و اگر از آن فاصله گرفت، یعنی جایی از قیمت‌گذاری
   * می‌لنگد. برای معامله‌گرِ آربیتراژی هم دقیقاً همین عدد به کار می‌آید.
   */
  const tickers = useMarket((m) => m.tickers)
  const usdtIrt = useMarket((m) => m.usdtIrt)
  const crossGap = useMemo(() => {
    if (!symbol.endsWith('IRT') || !usdtIrt) return null
    const base = symbol.replace(/IRT$/, '')
    if (base === 'USDT') return null
    const irt = tickers[`${base}IRT`]?.last
    const usd = tickers[`${base}USDT`]?.last
    if (!irt || !usd) return null
    const fair = usd * usdtIrt
    if (!fair) return null
    return (irt / fair - 1) * 100
  }, [tickers, usdtIrt, symbol])

  /* ─────────────────────────────────────────── خلاصه تحلیل */
  const summary = useMemo(() => technicalSummary(candles), [candles])

  /* ═════════════════════════════════════════ ترسیم‌های کاربر */
  const draw = useDrawings(symbol, user?.id ?? null)

  useEffect(() => {
    drawing.current = draw.tool !== 'none'
    return () => { drawing.current = false }
  }, [draw.tool, drawing])

  /* ─────────────────────────────────────────── عکس گرفتن از نمودار */
  const shot = useCallback(() => {
    try {
      const canvas = (main.current as any)?.takeScreenshot?.()
      if (!canvas) return
      const a = document.createElement('a')
      a.href = canvas.toDataURL('image/png')
      a.download = `${symbol}-${tf}.png`
      a.click()
    } catch { /* مرورگر اجازه نداد */ }
  }, [symbol, tf])

  const last = candles.length ? candles[candles.length - 1] : null
  const shown = hover ?? last
  const chg = shown && shown.open ? ((shown.close - shown.open) / shown.open) * 100 : 0

  const legend = useMemo(() => {
    if (!shown) return null
    const cell = (l: string, v: string, cls = 'text-slate-200') => (
      <span key={l} className="whitespace-nowrap">
        <span className="text-slate-500">{l} </span>
        <span className={clsx('num', cls)}>{v}</span>
      </span>
    )
    return (
      <div className="scroll-x pointer-events-none absolute right-3 top-2 z-10 flex gap-3 text-[11px]">
        {cell('باز', num(shown.open))}
        {cell('بیشترین', num(shown.high), 'text-up')}
        {cell('کمترین', num(shown.low), 'text-down')}
        {cell('بسته', num(shown.close))}
        {cell('تغییر', pct(chg), chg >= 0 ? 'text-up' : 'text-down')}
        {!compact && cell('حجم', num(shown.volume, 2))}
      </div>
    )
  }, [shown, chg, compact])

  const toolBtn = (t: { key: Tool; label: string; Icon: any }) => (
    <button key={t.key} title={t.label} onClick={() => draw.setTool(t.key)}
      className={clsx('tap grid shrink-0 place-items-center rounded-lg transition', iconBtn,
        draw.tool === t.key ? 'bg-brand-500/25 text-brand-100'
          : 'text-slate-500 hover:bg-white/[.07] hover:text-slate-200')}>
      <t.Icon className={touch ? 'h-4 w-4' : 'h-3.5 w-3.5'} />
    </button>
  )

  return (
    <div ref={rootBox} className="flex min-h-0 flex-1 flex-col">
      {/* ── نوار ابزار */}
      <div ref={toolsRef} className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-white/[.06] p-2">
        <div className="scroll-x flex max-w-full gap-1">
          {TFS.map((t) => (
            <button key={t.key} onClick={() => prefs.set({ tf: t.key })} title={t.label}
              className={clsx('shrink-0 rounded-lg text-xs font-medium transition', btn,
                tf === t.key ? 'bg-brand-500/20 text-brand-200 ring-1 ring-brand-500/40'
                  : 'text-slate-500 hover:bg-white/5 hover:text-slate-300')}>
              {compact ? t.short : t.label}
            </button>
          ))}
        </div>

        {/* `flex-wrap` عمدی است: روی صفحه ۳۲۰ پیکسلی، این گروه از کارت
            بیرون می‌زد و چون کارت برش می‌خورد، دکمه تمام‌صفحه ناپدید می‌شد. */}
        <div className="ms-auto flex flex-wrap items-center justify-end gap-1">
          <button onClick={() => prefs.set({ drawBar: !prefs.drawBar })} title="ابزار ترسیم"
            className={clsx('tap flex items-center justify-center gap-1 rounded-lg text-[11px] transition', btn,
              prefs.drawBar || draw.tool !== 'none'
                ? 'bg-brand-500/15 text-brand-200' : 'text-slate-400 hover:text-slate-200')}>
            <PenLine className="h-3.5 w-3.5" />
            {!compact && 'ترسیم'}
          </button>
          <button ref={settingsBtn} onClick={() => setMenu((m) => !m)}
            className={clsx('tap flex items-center justify-center gap-1 rounded-lg text-[11px] transition', btn,
              activeCount ? 'bg-brand-500/15 text-brand-200' : 'text-slate-400 hover:text-slate-200')}
            title="نوع نمودار و اندیکاتورها">
            <Settings2 className="h-3.5 w-3.5" />
            {compact ? (activeCount ? <span className="num">{num(activeCount)}</span> : '')
              : <>اندیکاتور{activeCount > 0 && <span className="num"> ({num(activeCount)})</span>}</>}
          </button>
          <button ref={signalBtn} onClick={() => setSignalOpen((v) => !v)}
            title="خلاصه تحلیل تکنیکال"
            className={clsx('tap flex items-center justify-center gap-1 rounded-lg text-[11px] transition', btn,
              summary
                ? (summary.all.score > 0.15 ? 'bg-up/15 text-up'
                  : summary.all.score < -0.15 ? 'bg-down/15 text-down'
                    : 'bg-white/[.06] text-slate-300')
                : 'text-slate-500')}>
            <Gauge className="h-3.5 w-3.5" />
            {compact ? '' : (summary ? summary.all.label : 'تحلیل')}
          </button>
          <button onClick={shot} title="عکس گرفتن از نمودار"
            className={clsx('tap grid place-items-center rounded-lg text-slate-500 transition hover:text-slate-200', iconBtn)}>
            <Camera className="h-4 w-4" />
          </button>
          <button onClick={() => main.current?.timeScale().fitContent()} title="بازگشت به نمای اصلی"
            className={clsx('tap grid place-items-center rounded-lg text-slate-500 transition hover:text-slate-200', iconBtn)}>
            <RotateCcw className="h-4 w-4" />
          </button>
          <button onClick={() => setFull((f) => !f)} title={full ? 'خروج از تمام‌صفحه' : 'تمام‌صفحه'}
            className={clsx('tap grid place-items-center rounded-lg transition',
              iconBtn, full ? 'bg-brand-500/20 text-brand-200' : 'text-slate-500 hover:text-slate-200')}>
            {full ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* ── نوار ترسیم (ردیف جداست، نه روی نمودار: روی گوشی جای نمودار را نمی‌گیرد) */}
      {prefs.drawBar && (
        <div ref={drawRef} className="scroll-x flex shrink-0 items-center gap-1 border-b border-white/[.06] bg-white/[.02] p-1.5">
          {TOOLS.map(toolBtn)}
          <span className="mx-1 h-5 w-px shrink-0 bg-white/10" />
          {DRAW_COLORS.map((c) => (
            <button key={c} title="رنگ ترسیم" onClick={() => draw.setColor(c)}
              className={clsx('shrink-0 rounded-full ring-offset-1 ring-offset-ink-900 transition',
                touch ? 'h-6 w-6' : 'h-4 w-4',
                draw.color === c ? 'ring-2 ring-white' : 'ring-1 ring-white/20')}
              style={{ background: c }} />
          ))}
          <span className="mx-1 h-5 w-px shrink-0 bg-white/10" />
          <button title="آهن‌ربا: چسبیدن به قیمت کندل" onClick={() => prefs.set({ magnet: !prefs.magnet })}
            className={clsx('tap grid shrink-0 place-items-center rounded-lg transition', iconBtn,
              prefs.magnet ? 'bg-brand-500/25 text-brand-100' : 'text-slate-500 hover:text-slate-200')}>
            <Magnet className="h-3.5 w-3.5" />
          </button>
          <button title={prefs.locked ? 'باز کردن قفل ترسیم‌ها' : 'قفل ترسیم‌ها'}
            onClick={() => prefs.set({ locked: !prefs.locked })}
            className={clsx('tap grid shrink-0 place-items-center rounded-lg transition', iconBtn,
              prefs.locked ? 'bg-brand-500/25 text-brand-100' : 'text-slate-500 hover:text-slate-200')}>
            {prefs.locked ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}
          </button>
          <button title={prefs.hideDraws ? 'نمایش ترسیم‌ها' : 'پنهان‌کردن ترسیم‌ها'}
            onClick={() => prefs.set({ hideDraws: !prefs.hideDraws })}
            className={clsx('tap grid shrink-0 place-items-center rounded-lg transition', iconBtn,
              prefs.hideDraws ? 'bg-brand-500/25 text-brand-100' : 'text-slate-500 hover:text-slate-200')}>
            {prefs.hideDraws ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
          <span className="mx-1 h-5 w-px shrink-0 bg-white/10" />
          <button title="برگرد" onClick={draw.undo} disabled={!draw.canUndo}
            className={clsx('tap grid shrink-0 place-items-center rounded-lg transition', iconBtn,
              draw.canUndo ? 'text-slate-400 hover:bg-white/[.07] hover:text-slate-100' : 'text-slate-700')}>
            <Undo2 className="h-3.5 w-3.5" />
          </button>
          <button title="جلو برو" onClick={draw.redo} disabled={!draw.canRedo}
            className={clsx('tap grid shrink-0 place-items-center rounded-lg transition', iconBtn,
              draw.canRedo ? 'text-slate-400 hover:bg-white/[.07] hover:text-slate-100' : 'text-slate-700')}>
            <Redo2 className="h-3.5 w-3.5" />
          </button>
          <button title="حذف ترسیم انتخاب‌شده" onClick={draw.removeSelected}
            className={clsx('tap grid shrink-0 place-items-center rounded-lg transition', iconBtn,
              draw.selected ? 'text-down hover:bg-white/[.07]' : 'text-slate-700')}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
          <button title="پاک‌کردن همه ترسیم‌ها" onClick={draw.clearAll}
            className={clsx('tap grid shrink-0 place-items-center rounded-lg transition', iconBtn,
              draw.items.length ? 'text-slate-400 hover:bg-white/[.07] hover:text-slate-100'
                : 'text-slate-700')}>
            <Eraser className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <Popover open={menu} onClose={() => setMenu(false)} anchor={settingsBtn} width={310}>
        <div className="max-h-[70vh] overflow-y-auto pe-1">
          <div className="mb-2 text-[11px] font-semibold text-slate-400">نوع نمودار</div>
          <div className="mb-3 grid grid-cols-2 gap-1">
            {TYPES.map((t) => (
              <button key={t.key} onClick={() => prefs.set({ type: t.key })}
                className={clsx('rounded-lg px-2 py-1.5 text-[11px] transition',
                  type === t.key ? 'bg-brand-500/20 text-brand-200 ring-1 ring-brand-500/40'
                    : 'text-slate-400 hover:bg-white/[.06]')}>
                {t.label}
              </button>
            ))}
          </div>

          <div className="mb-2 text-[11px] font-semibold text-slate-400">محور قیمت</div>
          <div className="mb-3 flex flex-wrap gap-1">
            {SCALES.map((s) => (
              <button key={s.key} onClick={() => prefs.set({ scale: s.key })}
                className={clsx('rounded-lg px-2 py-1 text-[11px] transition',
                  scale === s.key ? 'bg-white/10 text-white' : 'text-slate-500 hover:text-slate-300')}>
                {s.label}
              </button>
            ))}
            <button onClick={() => prefs.set({ invert: !invert })}
              className={clsx('rounded-lg px-2 py-1 text-[11px] transition',
                invert ? 'bg-white/10 text-white' : 'text-slate-500 hover:text-slate-300')}>
              معکوس
            </button>
          </div>

          <div className="mb-2 text-[11px] font-semibold text-slate-400">ابزارهای ویژه</div>
          <div className="mb-3 space-y-1">
            {([
              ['vprofile', 'نیم‌رخ حجم (VPVR)', 'در هر قیمت چقدر معامله شده — روی بازه دیده‌شده',
               prefs.vprofile, BarChart3],
              ['myTrades', 'معامله‌های من روی نمودار', 'دقیقاً کجا خریدید و کجا فروختید',
               prefs.myTrades, Flag],
            ] as const).map(([key, label, hint, val, Icon]) => (
              <button key={key} onClick={() => prefs.set({ [key]: !val } as any)}
                className="flex w-full items-start gap-2 rounded-xl px-2 py-2 text-right transition hover:bg-white/[.06]">
                <span className={clsx('mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded border',
                  val ? 'border-brand-400 bg-brand-500/30' : 'border-white/15')}>
                  {val && <Check className="h-3 w-3 text-brand-200" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1 text-xs text-slate-100">
                    <Icon className="h-3 w-3 text-slate-400" />{label}
                  </span>
                  <span className="block text-[10px] leading-5 text-slate-500">{hint}</span>
                </span>
              </button>
            ))}
          </div>

          <div className="mb-2 text-[11px] font-semibold text-slate-400">روی نمودار</div>
          {OVERLAYS.map((o) => (
            <button key={o.key} onClick={() => prefs.set({ overlays: toggle(overlays, o.key) })}
              className="flex w-full items-start gap-2 rounded-xl px-2 py-2 text-right transition hover:bg-white/[.06]">
              <span className={clsx('mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded border',
                on(o.key) ? 'border-brand-400 bg-brand-500/30' : 'border-white/15')}>
                {on(o.key) && <Check className="h-3 w-3 text-brand-200" />}
              </span>
              <span className="min-w-0">
                <span className="block text-xs text-slate-100">{o.label}</span>
                <span className="block text-[10px] leading-5 text-slate-500">{o.hint}</span>
              </span>
            </button>
          ))}

          <div className="mb-2 mt-3 text-[11px] font-semibold text-slate-400">پنل جداگانه</div>
          {PANES.map((k) => (
            <button key={k} onClick={() => prefs.set({ panes: toggle(panes, k) })}
              className="flex w-full items-start gap-2 rounded-xl px-2 py-2 text-right transition hover:bg-white/[.06]">
              <span className={clsx('mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded border',
                paneOn(k) ? 'border-brand-400 bg-brand-500/30' : 'border-white/15')}>
                {paneOn(k) && <Check className="h-3 w-3 text-brand-200" />}
              </span>
              <span className="min-w-0">
                <span className="block text-xs text-slate-100">{PANE_META[k].label}</span>
                <span className="block text-[10px] leading-5 text-slate-500">{PANE_META[k].hint}</span>
              </span>
            </button>
          ))}

          {type === 'heikin' && (
            <p className="mt-3 rounded-lg bg-amber-400/10 px-2 py-1.5 text-[10px] leading-5 text-amber-200/90">
              هایکن آشی قیمت واقعی نیست؛ میانگین‌گیری‌شده است. برای تشخیص روند
              خوب است، برای خواندن قیمت اجرا نه.
            </p>
          )}
          {user && (
            <p className="mt-3 border-t border-white/[.06] pt-2 text-[10px] leading-5 text-slate-500">
              سفارش‌های باز شما با خط‌چین روی نمودار نشان داده می‌شوند.
            </p>
          )}
        </div>
      </Popover>

      <Popover open={signalOpen} onClose={() => setSignalOpen(false)}
        anchor={signalBtn} width={300}>
        {summary ? (
          <div className="max-h-[70vh] overflow-y-auto pe-1">
            <div className="mb-2 grid grid-cols-3 gap-1 text-center">
              {([['میانگین‌ها', summary.ma], ['نوسان‌گرها', summary.osc],
                 ['مجموع', summary.all]] as const).map(([t, g]) => (
                <div key={t} className="rounded-xl border border-white/[.07] bg-white/[.03] p-2">
                  <div className="text-[10px] text-slate-500">{t}</div>
                  <div className={clsx('text-xs font-bold',
                    g.score > 0.15 ? 'text-up' : g.score < -0.15 ? 'text-down' : 'text-slate-200')}>
                    {g.label}
                  </div>
                  <div className="num text-[10px] text-slate-500">
                    {num(g.buy)} / {num(g.sell)}
                  </div>
                </div>
              ))}
            </div>
            <table className="w-full text-[11px]">
              <tbody>
                {summary.rows.map((r) => (
                  <tr key={r.key} className="border-t border-white/[.05]">
                    <td className="py-1.5 text-slate-300">{r.label}</td>
                    <td className="num py-1.5 text-slate-500" dir="ltr">{r.value}</td>
                    <td className={clsx('py-1.5 text-left font-medium',
                      r.vote === 1 ? 'text-up' : r.vote === -1 ? 'text-down' : 'text-slate-500')}>
                      {r.vote === 1 ? 'خرید' : r.vote === -1 ? 'فروش' : 'خنثی'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 rounded-lg bg-amber-400/10 px-2 py-1.5 text-[10px] leading-5 text-amber-200/90">
              این خلاصه، <b>توصیه معاملاتی نیست</b>. فقط وضعیت فعلی چند
              اندیکاتور را کنار هم می‌گذارد؛ هیچ‌کدام آینده را نمی‌گویند.
            </p>
          </div>
        ) : (
          <div className="p-2 text-xs text-slate-400">
            برای خلاصه تحلیل، دست‌کم ۳۰ کندل لازم است.
          </div>
        )}
      </Popover>

      {/* ── خودِ نمودار
          `absolute inset-0` عمدی است: ظرف همیشه اندازه معلوم دارد، پس
          کتابخانه هیچ‌وقت با ارتفاع صفر ساخته نمی‌شود — همان چیزی که روی
          گوشی باعث می‌شد نمودار نیمه‌کاره یا اصلاً بالا نیاید. */}
      <div className="relative min-h-[110px] flex-1">
        {legend}
        <div ref={mainBox} className="absolute inset-0" />

        {prefs.vprofile && (
          <VolumeProfileLayer chart={chartApi} series={seriesApi} candles={candles} />
        )}

        {/* لایه ترسیم دقیقاً روی نمودار می‌نشیند */}
        <DrawLayer
          chart={chartApi} series={seriesApi} bars={candles}
          tool={draw.tool} color={draw.color} items={draw.items}
          selected={draw.selected} magnet={prefs.magnet}
          locked={prefs.locked} hidden={prefs.hideDraws}
          onSelect={draw.setSelected}
          onCommit={draw.commit} onUpdate={draw.update}
          onToolDone={draw.done}
        />

        {draw.tool !== 'none' && (
          <div className="pointer-events-none absolute bottom-8 left-1/2 z-20 -translate-x-1/2 rounded-full border border-white/10 bg-ink-900/90 px-3 py-1 text-[10px] text-slate-300 backdrop-blur">
            {TOOLS.find((t) => t.key === draw.tool)?.label} — Esc برای لغو
          </div>
        )}

        {err && (
          <div className="absolute inset-0 grid place-items-center">
            <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-ink-900/80 px-3 py-2 text-xs text-slate-400">
              <Crosshair className="h-4 w-4" /> {err}
            </div>
          </div>
        )}
      </div>

      {shownPanes.map((k) => (
        <IndicatorPane key={k} paneKey={k} candles={candles} main={chartApi}
          height={paneH} scaleWidth={scaleW} />
      ))}

      <div ref={footRef} className="flex shrink-0 items-center justify-between gap-2 border-t border-white/[.06] px-3 py-1.5 text-[10px] text-slate-500">
        <span className="num" dir="ltr">{symbol}</span>
        {hiddenPanes > 0 && (
          <span className="whitespace-nowrap text-amber-300/80">
            <span className="num">{num(hiddenPanes)}</span> پنل به‌خاطر کمبود جا بسته است
          </span>
        )}
        {partial && !compact && (
          <span className="whitespace-nowrap text-slate-500"
            title="تاریخچه این بازه هنوز کامل نشده است. همه کندل‌های نشان‌داده‌شده واقعی‌اند؛ هرچه زمان بگذرد، نمودار عقب‌تر می‌رود.">
            تاریخچه در حال تکمیل
          </span>
        )}
        {crossGap != null && Math.abs(crossGap) >= 0.01 && (
          <span className="whitespace-nowrap"
            title="اختلاف قیمت این بازار تومانی با قیمت بازار تتریِ همین ارز ضرب‌در نرخ تتر. در حالت عادی نزدیک صفر است.">
            ناهم‌خوانی تومان/تتر{' '}
            <span className={clsx('num', crossGap > 0 ? 'text-down' : 'text-up')} dir="ltr">
              {crossGap > 0 ? '+' : ''}{num(crossGap, 2)}٪
            </span>
          </span>
        )}
        {left && !compact && (
          <span className="whitespace-nowrap">
            بسته‌شدن کندل: <span className="num" dir="ltr">{left}</span>
          </span>
        )}
        {note && !compact && <span className="truncate">{note}</span>}
        {!user && draw.items.length > 0 && (
          <span className="flex items-center gap-1 whitespace-nowrap text-amber-300/80">
            <Lock className="h-3 w-3" /> برای ماندگاری ترسیم‌ها وارد شوید
          </span>
        )}
        <span className="whitespace-nowrap">{quote === 'IRT' ? 'تومان' : 'تتر'}</span>
      </div>
    </div>
  )
}

const toggle = <T,>(arr: T[], k: T) => (arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k])

/* ══════════════════════════════════════════════════ تنظیمات نمودار */

type Prefs = {
  tf: string
  type: ChartType
  overlays: Overlay[]
  panes: PaneKey[]
  scale: ScaleKey
  invert: boolean
  magnet: boolean
  locked: boolean
  hideDraws: boolean
  drawBar: boolean
  vprofile: boolean
  myTrades: boolean
}

const PREF_KEY = 'az_chart_prefs'
const DEFAULT_PREFS: Prefs = {
  tf: '1h', type: 'candle', overlays: ['ma'], panes: [], scale: 'normal',
  invert: false, magnet: false, locked: false, hideDraws: false, drawBar: false,
  vprofile: false, myTrades: true,
}

/**
 * تنظیمات نمودار روی همین مرورگر می‌ماند.
 *
 * کسی که هر روز با نمودار کار می‌کند، دوست ندارد هر بار باز کردن صفحه
 * دوباره بازه و اندیکاتورهایش را بچیند. این‌ها ترجیح نمایشی‌اند نه داده
 * حساب کاربر، پس جایشان همین‌جاست و به سرور نمی‌روند.
 */
function useChartPrefs() {
  const [prefs, setPrefs] = useState<Prefs>(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(PREF_KEY) || '{}')
      const merged = { ...DEFAULT_PREFS, ...(raw && typeof raw === 'object' ? raw : {}) }
      // اعتبارسنجی: مقدارِ کهنه یا دستکاری‌شده نباید نمودار را خراب کند
      if (!TFS.some((t) => t.key === merged.tf)) merged.tf = DEFAULT_PREFS.tf
      if (!TYPES.some((t) => t.key === merged.type)) merged.type = DEFAULT_PREFS.type
      if (!SCALES.some((s) => s.key === merged.scale)) merged.scale = DEFAULT_PREFS.scale
      merged.overlays = (Array.isArray(merged.overlays) ? merged.overlays : [])
        .filter((o: any) => OVERLAYS.some((x) => x.key === o))
      merged.panes = (Array.isArray(merged.panes) ? merged.panes : [])
        .filter((p: any) => PANES.includes(p))
      return merged
    } catch { return DEFAULT_PREFS }
  })

  // نوشتن در حافظه مرورگر **بیرون** از تابع به‌روزرسانی انجام می‌شود؛
  // React در حالت سخت‌گیرانه آن تابع را دو بار صدا می‌زند و هر اثر جانبی
  // داخلش دو بار اجرا می‌شود.
  const ref = useRef(prefs)
  ref.current = prefs
  const set = useCallback((patch: Partial<Prefs>) => {
    const next = { ...ref.current, ...patch }
    ref.current = next
    try { localStorage.setItem(PREF_KEY, JSON.stringify(next)) } catch { /* حافظه پر */ }
    setPrefs(next)
  }, [])

  return { ...prefs, set }
}

/* ══════════════════════════════════════════════════ ذخیره ترسیم‌ها */

const HISTORY_MAX = 50

/**
 * ترسیم‌های کاربر، برای هر بازار جدا.
 *
 * وارد شده باشد → روی حساب (`/user/chart-drawings/{symbol}`) ذخیره می‌شود و
 * از هر دستگاهی برمی‌گردد. وارد نشده باشد → روی همان مرورگر می‌ماند تا کارش
 * گم نشود؛ و **به‌محض ورود**، همان‌ها به حسابش منتقل می‌شوند.
 *
 * `dirty` لازم است: بدون آن، همان داده‌ای که تازه از سرور گرفته‌ایم بلافاصله
 * دوباره به سرور فرستاده می‌شد — نوشتن بی‌دلیل روی دیتابیس در هر بار باز
 * کردن صفحه.
 */
function useDrawings(symbol: string, userId: number | string | null) {
  const [items, setItems] = useState<Drawing[]>([])
  const [tool, setTool] = useState<Tool>('none')
  const [color, setColor] = useState(DRAW_COLORS[0])
  const [selected, setSelected] = useState<string | null>(null)
  // تاریخچه در ref نگه داشته می‌شود، نه در state — دلیلش پایین‌تر آمده.
  // `histVer` فقط برای این است که دکمه‌های «برگرد / جلو برو» تازه شوند.
  const itemsRef = useRef<Drawing[]>([])
  const past = useRef<Drawing[][]>([])
  const future = useRef<Drawing[][]>([])
  const [histVer, setHistVer] = useState(0)
  const dirty = useRef(false)

  const lsKey = `az_draw_${symbol}`
  const local = useCallback((): Drawing[] => {
    try {
      return sanitizeDrawings(JSON.parse(localStorage.getItem(lsKey) || '[]'))
    } catch { return [] }
  }, [lsKey])

  useEffect(() => {
    let alive = true
    dirty.current = false
    setSelected(null)
    setItems([])
    itemsRef.current = []
    past.current = []; future.current = []; setHistVer((v) => v + 1)
    if (!userId) { const v = local(); setItems(v); itemsRef.current = v; return }
    get(`/user/chart-drawings/${symbol}`).then((r: any) => {
      if (!alive) return
      // اگر کاربر در همان چند صدم ثانیه‌ای که پاسخ در راه بود چیزی کشیده،
      // پاسخِ سرور نباید کارش را پاک کند.
      if (dirty.current) return
      const server = sanitizeDrawings(r?.items)
      const mine = local()
      if (!server.length && mine.length) {
        // ترسیم‌هایی که پیش از ورود کشیده بود، به حسابش منتقل می‌شود.
        //
        // نسخه مرورگر **فقط پس از موفق‌شدن انتقال** پاک می‌شود: اگر زودتر
        // پاکش می‌کردیم و ذخیره ناموفق می‌ماند (یا کاربر همان لحظه صفحه را
        // عوض می‌کرد)، ترسیم‌ها از هر دو طرف می‌رفتند. و پاک‌کردنش لازم
        // است، وگرنه با ورود کاربر بعدی روی همین مرورگر، همین ترسیم‌ها به
        // حساب او هم منتقل می‌شد.
        setItems(mine)
        itemsRef.current = mine
        put(`/user/chart-drawings/${symbol}`, { items: mine })
          .then(() => { try { localStorage.removeItem(lsKey) } catch { /* بی‌اهمیت */ } })
          .catch(() => { /* دفعه بعد */ })
      } else { setItems(server); itemsRef.current = server }
    }).catch(() => {
      if (alive && !dirty.current) { const v = local(); setItems(v); itemsRef.current = v }
    })
    return () => { alive = false }
  }, [symbol, userId, local, lsKey])

  // ذخیره با تأخیر: هنگام جابه‌جایی یک خط، ده‌ها رویداد پشت‌سرهم می‌آید و
  // بدون این تأخیر، هر کدام یک درخواست جدا می‌شد.
  useEffect(() => {
    if (!dirty.current) return
    const id = setTimeout(() => {
      // وارد شده ⇒ فقط روی حساب. نگه‌داشتن یک نسخه محلی هم وسوسه‌انگیز است
      // ولی همان چیزی است که ترسیم‌ها را بین حساب‌های یک مرورگر جابه‌جا
      // می‌کند. حافظه مرورگر فقط برای کاربر وارد‌نشده است.
      if (userId) put(`/user/chart-drawings/${symbol}`, { items }).catch(() => { /* بعداً */ })
      else {
        try { localStorage.setItem(lsKey, JSON.stringify(items)) } catch { /* حافظه پر */ }
      }
    }, 700)
    return () => clearTimeout(id)
  }, [items, symbol, userId, lsKey])

  /*
   * تاریخچه «برگرد / جلو برو».
   *
   * نکته‌ای که یک بار گرفتارش شدم: در حالت سخت‌گیرانه React (که در این
   * پروژه روشن است) تابعِ به‌روزرسانیِ `setState` **دو بار** صدا زده
   * می‌شود. اگر داخل آن `setState` دیگری بگذاریم، هر کار کاربر دو پله
   * تاریخچه می‌ساخت: «برگرد» دو بار فشردن لازم داشت و «جلو برو» اصلاً کار
   * نمی‌کرد.
   *
   * پس خودِ تاریخچه در `ref` است و همه‌چیز بیرون از هر به‌روزرسانی و به
   * ترتیب انجام می‌شود. `histVer` فقط یک شمارنده است تا دکمه‌ها فعال و
   * غیرفعال شوند.
   */
  const bump = useCallback(() => setHistVer((v) => v + 1), [])

  const pushHistory = useCallback((cur: Drawing[]) => {
    past.current = [...past.current, cur].slice(-HISTORY_MAX)
    future.current = []
  }, [])

  const change = useCallback((fn: (v: Drawing[]) => Drawing[]) => {
    dirty.current = true
    const cur = itemsRef.current
    const next = fn(cur)
    pushHistory(cur)
    itemsRef.current = next
    setItems(next)
    bump()
  }, [pushHistory, bump])

  const commit = useCallback((d: Drawing) => {
    change((v) => [...v, d])
    setSelected(d.id)
  }, [change])

  // جابه‌جایی، ده‌ها رویداد پشت‌سرهم می‌سازد؛ اگر هرکدام یک پله تاریخچه
  // می‌شد، «برگرد» عملاً بی‌فایده بود. پس فقط شروعِ هر جابه‌جایی ثبت می‌شود.
  const dragging = useRef<string | null>(null)
  const update = useCallback((d: Drawing) => {
    dirty.current = true
    const cur = itemsRef.current
    if (dragging.current !== d.id) {
      dragging.current = d.id
      pushHistory(cur)
      bump()
      setTimeout(() => { dragging.current = null }, 400)
    }
    const next = cur.map((x) => (x.id === d.id ? d : x))
    itemsRef.current = next
    setItems(next)
  }, [pushHistory, bump])

  const removeSelected = useCallback(() => {
    if (!selected) return
    change((v) => v.filter((x) => x.id !== selected))
    setSelected(null)
  }, [selected, change])

  const clearAll = useCallback(() => {
    change(() => [])
    setSelected(null)
    try { localStorage.removeItem(lsKey) } catch { /* بی‌اهمیت */ }
  }, [change, lsKey])

  const undo = useCallback(() => {
    if (!past.current.length) return
    const prev = past.current[past.current.length - 1]
    past.current = past.current.slice(0, -1)
    future.current = [...future.current, itemsRef.current].slice(-HISTORY_MAX)
    dirty.current = true
    itemsRef.current = prev
    setItems(prev)
    setSelected(null)
    bump()
  }, [bump])

  const redo = useCallback(() => {
    if (!future.current.length) return
    const next = future.current[future.current.length - 1]
    future.current = future.current.slice(0, -1)
    past.current = [...past.current, itemsRef.current].slice(-HISTORY_MAX)
    dirty.current = true
    itemsRef.current = next
    setItems(next)
    setSelected(null)
    bump()
  }, [bump])

  // ارجاع ثابت: اگر هر بار تابع تازه‌ای می‌ساختیم، شنونده کیبورد لایه
  // ترسیم با هر حرکت نشانگر روی نمودار برداشته و دوباره گذاشته می‌شد.
  const done = useCallback(() => setTool('none'), [])

  // حذف با کلید Delete و برگشت با Ctrl+Z، مثل هر برنامه ترسیمی دیگری
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      const el = document.activeElement
      const typing = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement
      if (typing) return
      if ((e.key === 'Delete' || e.key === 'Backspace') && selected) {
        e.preventDefault()
        removeSelected()
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault()
        if (e.shiftKey) redo(); else undo()
      }
    }
    window.addEventListener('keydown', key)
    return () => window.removeEventListener('keydown', key)
  }, [selected, removeSelected, undo, redo])

  return {
    items, tool, setTool, color, setColor, selected, setSelected,
    commit, update, removeSelected, clearAll, done,
    undo, redo,
    // `histVer` عمداً خوانده می‌شود تا با هر تغییر تاریخچه، دکمه‌ها دوباره
    // ارزیابی شوند (خودِ تاریخچه در ref است و رندر را تازه نمی‌کند).
    canUndo: histVer >= 0 && past.current.length > 0,
    canRedo: histVer >= 0 && future.current.length > 0,
  }
}

/** داده قیمت را متناسب با نوع نمودار می‌ریزد */
function applyPrice(s: ISeriesApi<any>, rows: Candle[], type: ChartType) {
  const src = type === 'heikin' ? heikinAshi(rows) : rows
  if (type === 'candle' || type === 'bar' || type === 'hollow' || type === 'heikin') {
    s.setData(src.map((c) => ({
      time: c.time, open: c.open, high: c.high, low: c.low, close: c.close,
    })) as any)
    return
  }
  if (type === 'baseline') {
    // خط پایه باید نسبت به چیزی سنجیده شود؛ منطقی‌ترین مرجع، قیمتِ نخستین
    // کندلِ همین نما است: بالای آن سبز، پایینش قرمز.
    try { s.applyOptions({ baseValue: { type: 'price', price: src[0].close } } as any) }
    catch { /* نسخه کتابخانه این گزینه را ندارد */ }
  }
  s.setData(src.map((c) => ({ time: c.time, value: c.close })) as any)
}
